import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { Shield, FileCheck, Ruler, ClipboardCheck, Heart, ArrowRight, Plane, ExternalLink, BadgeCheck } from "lucide-react";
import usePageMeta from "@/lib/usePageMeta";

const HERO_IMG =
    "https://images.pexels.com/photos/29353711/pexels-photo-29353711.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1400&w=2000";

const easeOut = [0.16, 1, 0.3, 1];

const SECTIONS = [
    {
        n: "01",
        Icon: FileCheck,
        title: "Official Certifications & Registration",
        lede: "I maintain up-to-date legal credentials for every mission:",
        items: [
            { term: "Operator ID", body: "Proudly displayed on the chassis of my aircraft as per CAA requirements." },
            { term: "Flyer ID", body: "I have passed the official CAA competency testing to ensure a deep understanding of UK airspace regulations." },
            { term: "Insurance", body: "I carry full Public Liability and Indemnity insurance (via FPVUK), ensuring that every flight is protected." },
        ],
    },
    {
        n: "02",
        Icon: Ruler,
        title: "The Drone Code — Operational Limits",
        lede: "I fly strictly within the UK Drone Code to ensure the safety of the public and other airspace users:",
        items: [
            { term: "Altitude", body: "Flights are capped at a maximum of 120m (400ft) above the surface." },
            { term: "Visual Line of Sight (VLOS)", body: "I maintain eye contact with the aircraft at all times to monitor for obstacles and other aircraft." },
            { term: "Safety Distances", body: "I maintain a minimum horizontal distance of 50m from uninvolved persons and never fly directly over crowds or congested areas." },
            { term: "Night Flying", body: "When capturing the beauty of the Highlands at night, I use high-visibility strobe lighting (flashing green and red) to maintain legal compliance and orientation." },
        ],
    },
    {
        n: "03",
        Icon: ClipboardCheck,
        title: "Pre-Flight Protocols & Permissions",
        lede: 'No mission at DTScotslad launches without a "Green Light" from our pre-flight checks:',
        items: [
            { term: "Airspace Analysis", body: "Before every takeoff, I use industry-standard tools like Drone Assist (Altitude Angel) and the DJI Fly App to check for Flight Restriction Zones (FRZ), temporary flight restrictions, and proximity to airports like Perth (Scone)." },
            { term: "Site-Specific Risk Assessments", body: "For every location — from the rugged walls of Balvaird Castle to the banks of the River Tay — I conduct a thorough risk assessment identifying hazards like nesting birds, historic masonry, and weather conditions." },
            { term: "Landowner Permissions", body: "I respect property rights and Historic Environment Scotland guidelines. I take off and land only from permitted areas and seek specific permissions where required for protected sites." },
        ],
    },
    {
        n: "04",
        Icon: Heart,
        title: "Privacy and Ethics",
        lede: "I am committed to ethical cinematography:",
        items: [
            { term: "Privacy", body: "I respect the privacy of individuals and do not film private residences or people without consent." },
            { term: "Wildlife", body: "Scotland's wildlife is a national treasure. I never disturb nesting birds or livestock, and I will ground a mission immediately if animals show signs of distress." },
            { term: "Legacy Preservation", body: "When filming ancient monuments, I maintain safe distances to ensure the preservation of Scotland's historic stone for generations to come." },
        ],
    },
];

/* ---------- Motion helpers ---------- */
function MaskedLine({ children, delay = 0, className = "" }) {
    return (
        <span className="inline-block overflow-hidden align-bottom" style={{ lineHeight: 1, paddingRight: "0.08em", paddingBottom: "0.05em" }}>
            <motion.span
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay, ease: easeOut }}
                className={`inline-block ${className}`}
            >
                {children}
            </motion.span>
        </span>
    );
}

function Reveal({ children, delay = 0, className = "" }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-15% 0px -10% 0px" });
    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 24 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.9, delay, ease: easeOut }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

/* ---------- Hero ---------- */
function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

    return (
        <section ref={ref} className="relative min-h-[85svh] w-full overflow-hidden grain" style={{ position: "relative" }} data-testid="safety-hero">
            <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
                <img src={HERO_IMG} alt="Scottish glen" className="w-full h-full object-cover slow-zoom-n" />
                <div className="absolute inset-0 bg-gradient-to-b from-[var(--sb-bg)]/60 via-[var(--sb-bg)]/30 to-[var(--sb-bg)]" />
                <div className="absolute inset-0" style={{ background: "radial-gradient(55% 45% at 30% 55%, rgba(107,167,222,0.12) 0%, rgba(0,0,0,0) 60%)" }} />
            </motion.div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-14 flex items-center justify-between text-xs tracking-[0.28em] uppercase text-[var(--sb-text-dim)]">
                <span className="flex items-center gap-2">
                    <Shield size={12} className="text-[var(--sb-brand)]" />
                    <MaskedLine delay={0.05}>Compliance · CAA · Ethics</MaskedLine>
                </span>
                <span className="hidden sm:inline"><MaskedLine delay={0.15}>Operator ID · Flyer ID · Insured</MaskedLine></span>
            </div>

            <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 pt-24 pb-20 lg:pt-32 lg:pb-28">
                <div className="overline mb-4">
                    <MaskedLine delay={0.1}>The Pilot&rsquo;s Code</MaskedLine>
                </div>
                <h1 className="font-serif font-light tracking-tighter mb-6" style={{ fontSize: "clamp(2.6rem, 7.5vw, 7.5rem)", lineHeight: 0.94 }} data-testid="safety-title">
                    <div>
                        <MaskedLine delay={0.15}>Safety is the</MaskedLine>{" "}
                        <MaskedLine delay={0.3}>
                            <span className="italic text-[var(--sb-brand)]">first waypoint.</span>
                        </MaskedLine>
                    </div>
                </h1>
                <div className="max-w-2xl text-[var(--sb-text-dim)] text-lg lg:text-xl leading-relaxed">
                    <MaskedLine delay={0.55}>
                        At DTScotslad the mission is to capture the splendor of Scotland — but no flight leaves the ground until the law, the environment, and the public are respected.
                    </MaskedLine>
                </div>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.85, ease: easeOut }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <div className="border border-[var(--sb-border)] px-4 py-3 bg-[var(--sb-surface)]/70 backdrop-blur-md">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--sb-text-muted)]">Aircraft</div>
                        <div className="mt-1 font-serif text-lg flex items-center gap-2">
                            <Plane size={14} className="text-[var(--sb-brand)]" /> DJI Air 3 · 720g
                        </div>
                    </div>
                    <div className="border border-[var(--sb-border)] px-4 py-3 bg-[var(--sb-surface)]/70 backdrop-blur-md">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--sb-text-muted)]">Regulator</div>
                        <div className="mt-1 font-serif text-lg">UK CAA · A2 CofC ready</div>
                    </div>
                    <div className="border border-[var(--sb-border)] px-4 py-3 bg-[var(--sb-surface)]/70 backdrop-blur-md">
                        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--sb-text-muted)]">Insurance</div>
                        <div className="mt-1 font-serif text-lg">FPVUK · Full PL &amp; PI</div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}

/* ---------- Section blocks ---------- */
function CodeSection({ section, index }) {
    const flip = index % 2 === 1;
    const { Icon } = section;
    return (
        <section
            className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 border-t border-[var(--sb-border)]"
            data-testid={`safety-section-${section.n}`}
        >
            <div className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
                {/* Left rail (numeral + icon) */}
                <Reveal className="lg:col-span-4">
                    <div className="relative pl-6 lg:pl-0">
                        <div className="flex items-center gap-4 mb-6">
                            <span className="w-12 h-px bg-[var(--sb-brand)]" />
                            <span className="text-xs tracking-[0.28em] uppercase text-[var(--sb-brand)]">
                                {section.n} / 04
                            </span>
                        </div>
                        <div
                            className="font-serif italic leading-none text-[var(--sb-brand)]/70 mb-6"
                            style={{ fontSize: "clamp(4rem, 9vw, 8rem)" }}
                        >
                            {section.n}
                        </div>
                        <div className="w-14 h-14 border border-[var(--sb-border)] flex items-center justify-center bg-[var(--sb-surface)]">
                            <Icon size={22} className="text-[var(--sb-brand)]" />
                        </div>
                    </div>
                </Reveal>

                {/* Right content */}
                <div className="lg:col-span-8">
                    <Reveal delay={0.05}>
                        <h2 className="font-serif tracking-tight leading-[1.02] mb-6" style={{ fontSize: "clamp(2rem, 4vw, 3.4rem)" }}>
                            {section.title}
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <p className="text-[var(--sb-text-dim)] text-lg leading-relaxed max-w-2xl mb-10">
                            {section.lede}
                        </p>
                    </Reveal>
                    <div className="space-y-6">
                        {section.items.map((it, i) => (
                            <Reveal key={i} delay={0.15 + i * 0.05}>
                                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 border-t border-[var(--sb-border)] pt-6">
                                    <div className="md:col-span-4">
                                        <div className="overline">{it.term}</div>
                                    </div>
                                    <div className="md:col-span-8 text-[var(--sb-text-dim)] text-base lg:text-lg leading-[1.75]">
                                        {it.body}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ---------- Verify credentials ---------- */
const CREDENTIALS = [
    {
        label: "CAA — Drone & Model Aircraft Registration",
        term: "Operator & Flyer ID",
        body: "Verify UK Civil Aviation Authority registration for commercial and hobbyist drone operators. Ask for my Operator ID to look up.",
        href: "https://register-drones.caa.co.uk/",
        cta: "CAA Registration Portal",
    },
    {
        label: "FPVUK — Public Liability & Indemnity Insurance",
        term: "Insurance policy",
        body: "£5m Public Liability & Indemnity policy carried via FPVUK — the recognised BMFA-linked body for UK drone insurance.",
        href: "https://www.fpvuk.org/",
        cta: "FPVUK",
    },
    {
        label: "Drone Assist — Live UK Airspace",
        term: "Pre-flight tool",
        body: "The industry-standard app I use before every flight — checks Flight Restriction Zones, NOTAMs and airport proximity in real time.",
        href: "https://www.altitudeangel.com/apps/drone-assist",
        cta: "Drone Assist by Altitude Angel",
    },
    {
        label: "The UK Drone & Model Aircraft Code",
        term: "Legal framework",
        body: "The full UK Drone Code — the rulebook every flight at DTScotslad complies with, including the 120m altitude cap and 50m safety distance.",
        href: "https://register-drones.caa.co.uk/drone-code",
        cta: "Read the Drone Code",
    },
];

function VerifyCredentials() {
    return (
        <section
            className="max-w-7xl mx-auto px-6 lg:px-12 py-20 lg:py-28 border-t border-[var(--sb-border)]"
            data-testid="safety-verify"
        >
            <Reveal>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-10 items-end">
                    <div className="lg:col-span-7">
                        <div className="flex items-center gap-3 mb-4 text-[var(--sb-brand)]">
                            <BadgeCheck size={18} />
                            <span className="text-xs tracking-[0.32em] uppercase">Verify my credentials</span>
                        </div>
                        <h2
                            className="font-serif tracking-tight leading-[1.02]"
                            style={{ fontSize: "clamp(2rem, 4vw, 3.6rem)" }}
                        >
                            Stated compliance is one thing.<br />
                            <span className="italic text-[var(--sb-brand)]">Auditable compliance is another.</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-5 text-[var(--sb-text-dim)] text-lg leading-relaxed">
                        Commercial clients — verify the paperwork directly with the authorities and providers below. Ask me for my Operator ID and policy number any time.
                    </div>
                </div>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {CREDENTIALS.map((c, i) => (
                    <Reveal key={c.href} delay={0.05 + i * 0.05}>
                        <a
                            href={c.href}
                            target="_blank"
                            rel="noreferrer"
                            className="group block h-full border border-[var(--sb-border)] bg-[var(--sb-surface)] p-6 lg:p-8 transition-colors duration-500 hover:border-[var(--sb-brand)] relative overflow-hidden"
                            data-testid={`safety-verify-${i}`}
                        >
                            <div className="flex items-start justify-between gap-4 mb-4">
                                <span className="text-[10px] tracking-[0.28em] uppercase text-[var(--sb-brand)]">{c.term}</span>
                                <ExternalLink size={14} className="text-[var(--sb-text-muted)] group-hover:text-[var(--sb-brand)] transition-colors mt-0.5" />
                            </div>
                            <h3 className="font-serif text-2xl leading-tight mb-3 group-hover:text-[var(--sb-brand)] transition-colors">
                                {c.label}
                            </h3>
                            <p className="text-sm text-[var(--sb-text-dim)] leading-relaxed mb-6">{c.body}</p>
                            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[var(--sb-text)] group-hover:gap-3 transition-all">
                                {c.cta} <ArrowRight size={12} />
                            </span>
                        </a>
                    </Reveal>
                ))}
            </div>
        </section>
    );
}

/* ---------- Closing ---------- */
function Closing() {
    return (
        <section className="max-w-5xl mx-auto px-6 lg:px-12 py-24 lg:py-32 border-t border-[var(--sb-border)]" data-testid="safety-closing">
            <Reveal>
                <div className="overline mb-4">A closing note from the pilot</div>
            </Reveal>
            <Reveal delay={0.1}>
                <h2 className="font-serif tracking-tight leading-[1.02] mb-8" style={{ fontSize: "clamp(2.2rem, 5vw, 4.4rem)" }}>
                    Through these digital wings, I am reclaiming the Highlands —<br />
                    <span className="italic text-[var(--sb-brand)]">responsibly, legally, and professionally.</span>
                </h2>
            </Reveal>
            <Reveal delay={0.2}>
                <p className="font-serif italic text-2xl lg:text-3xl text-[var(--sb-text)] mb-12">
                    Get it did. Safely. Thank you.
                </p>
            </Reveal>

            <Reveal delay={0.3}>
                <div className="flex flex-wrap gap-3">
                    <Link to="/book" className="btn-primary" data-testid="safety-book-cta">
                        Book a compliant shoot <ArrowRight size={16} />
                    </Link>
                    <Link to="/contact" className="btn-outline" data-testid="safety-contact-cta">
                        Ask a safety question
                    </Link>
                </div>
            </Reveal>
        </section>
    );
}

/* ---------- Page ---------- */
export default function Safety() {
    usePageMeta({
        title: "Safety & Compliance — DTScotslad",
        description: "The Pilot's Code — CAA registration, insurance, UK Drone Code, pre-flight protocols, and ethical cinematography. Every DTScotslad flight is compliant, insured and respectful of Scotland's people, wildlife and heritage.",
        ogImage: HERO_IMG,
    });

    return (
        <div data-testid="safety-page">
            <Hero />
            {SECTIONS.map((s, i) => (
                <CodeSection key={s.n} section={s} index={i} />
            ))}
            <VerifyCredentials />
            <Closing />
        </div>
    );
}
